import { useState, type ReactNode } from 'react'
import { Avatar } from '../../components/index.js'
import { formatMessageTimestamp } from '../../lib/utils.js'
import { MESSAGE_EDIT_WINDOW_MS } from '../../lib/constants.js'
import { MessageActions } from './MessageActions.js'
import { ImageLightbox } from './ImageLightbox.js'
import type { ChatMessage } from '../../types/chat.js'

/** Render @mentions in text as highlighted spans */
function renderContentWithMentions(text: string): ReactNode[] {
  const parts = text.split(/(@\w+(?:\s\w+)?)/g)
  return parts.map((part, i) =>
    part.startsWith('@') ? (
      <span key={i} className="font-medium text-(--color-primary)">{part}</span>
    ) : (
      <span key={i}>{part}</span>
    ),
  )
}

/** Get file icon and label based on MIME type */
function getFileInfo(type: string, name: string): { icon: string; label: string } {
  const ext = name.split('.').pop()?.toLowerCase() ?? ''
  if (type.includes('pdf') || ext === 'pdf') return { icon: '📕', label: 'PDF' }
  if (type.includes('word') || ext === 'doc' || ext === 'docx') return { icon: '📘', label: 'DOC' }
  if (type.includes('sheet') || type.includes('excel') || ext === 'xls' || ext === 'xlsx') return { icon: '📗', label: 'XLS' }
  if (type.includes('presentation') || ext === 'ppt' || ext === 'pptx') return { icon: '📙', label: 'PPT' }
  if (type.includes('text') || ext === 'txt' || ext === 'csv') return { icon: '📄', label: 'TXT' }
  if (type.includes('zip') || type.includes('rar') || type.includes('compressed')) return { icon: '🗜️', label: 'ZIP' }
  if (type.includes('video')) return { icon: '🎬', label: 'Video' }
  return { icon: '📎', label: ext.toUpperCase() || 'Arquivo' }
}

/** Single check — sent */
function CheckIcon({ className }: { className?: string }) {
  return (
    <svg className={`h-3.5 w-3.5 ${className ?? ''}`} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5} strokeLinecap="round" strokeLinejoin="round">
      <path d="M20 6 9 17l-5-5" />
    </svg>
  )
}

/** Double check — delivered/read */
function CheckCheckIcon({ className }: { className?: string }) {
  return (
    <svg className={`h-3.5 w-3.5 ${className ?? ''}`} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5} strokeLinecap="round" strokeLinejoin="round">
      <path d="M18 6 7 17l-5-5" />
      <path d="m22 10-9.5 9.5L10 17" />
    </svg>
  )
}

interface MessageBubbleProps {
  message: ChatMessage
  isMine: boolean
  senderName: string
  senderAvatar?: string | null
  showSender: boolean
  readCount: number
  onEdit: (messageId: string, content: string) => void
  onDelete: (messageId: string) => void
}

export function MessageBubble({
  message,
  isMine,
  senderName,
  senderAvatar,
  showSender,
  readCount,
  onEdit,
  onDelete,
}: MessageBubbleProps) {
  const [isHovered, setIsHovered] = useState(false)
  const [lightboxImage, setLightboxImage] = useState<{ url: string; name: string } | null>(null)
  const canModify =
    isMine && Date.now() - new Date(message.createdAt).getTime() < MESSAGE_EDIT_WINDOW_MS

  return (
    <>
      <div
        className={`group flex gap-2 px-4 py-1 ${isMine ? 'flex-row-reverse' : ''}`}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        {/* Avatar */}
        {!isMine && (
          <div className="mt-auto w-8 shrink-0">
            {showSender && <Avatar name={senderName} avatarUrl={senderAvatar} size="sm" />}
          </div>
        )}

        <div className={`max-w-[70%] ${isMine ? 'items-end' : 'items-start'}`}>
          {/* Sender name */}
          {showSender && !isMine && (
            <p className="mb-0.5 text-xs font-medium text-(--color-primary)">{senderName}</p>
          )}

          {/* Bubble */}
          <div className="relative">
            <div
              className={`rounded-lg px-3 py-2 text-sm ${
                isMine
                  ? 'bg-(--color-bubble-sent) text-(--color-text-primary)'
                  : 'bg-(--color-bubble-received) text-(--color-text-primary)'
              }`}
            >
              {/* Attachments */}
              {message.attachments?.map((att, i) => (
                <div key={i} className="mb-1">
                  {att.type.startsWith('image/') ? (
                    <button
                      onClick={() => setLightboxImage({ url: att.url, name: att.name })}
                      className="block cursor-pointer rounded-md overflow-hidden hover:opacity-90 transition-opacity"
                    >
                      <img
                        src={att.url}
                        alt={att.name}
                        className="max-h-64 max-w-60 rounded-md object-contain"
                        loading="lazy"
                      />
                    </button>
                  ) : att.type.startsWith('audio/') ? (
                    <audio controls className="max-w-full" preload="metadata">
                      <source src={att.url} type={att.type} />
                    </audio>
                  ) : (
                    /* Generic file — card with download */
                    <a
                      href={att.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      download={att.name}
                      className={`flex items-center gap-3 rounded-lg p-2.5 no-underline transition-colors ${
                        isMine
                          ? 'bg-black/5 hover:bg-black/10'
                          : 'bg-(--color-bg-tertiary) hover:bg-(--color-bg-secondary)'
                      }`}
                    >
                      <span className="text-2xl shrink-0">{getFileInfo(att.type, att.name).icon}</span>
                      <div className="flex-1 min-w-0 overflow-hidden">
                        <p className="truncate text-xs font-medium text-(--color-text-primary)">
                          {att.name}
                        </p>
                        <p className="text-[10px] text-(--color-text-muted)">
                          {getFileInfo(att.type, att.name).label}
                        </p>
                      </div>
                      <svg className="h-4 w-4 shrink-0 text-(--color-text-muted)" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5M16.5 12L12 16.5m0 0L7.5 12m4.5 4.5V3" />
                      </svg>
                    </a>
                  )}
                </div>
              ))}

              {message.content && (
                <p className="whitespace-pre-wrap break-words">
                  {renderContentWithMentions(message.content)}
                </p>
              )}

              {/* Meta */}
              <div className={`mt-1 flex items-center gap-1 ${isMine ? 'justify-end' : ''}`}>
                {message.editedAt && (
                  <span className="text-[10px] text-(--color-text-muted) italic">editada</span>
                )}
                <span className="text-[10px] text-(--color-text-muted)">
                  {formatMessageTimestamp(message.createdAt)}
                </span>
                {isMine && (
                  readCount > 0
                    ? <CheckCheckIcon className="text-[#7EB6FF]" />
                    : <CheckCheckIcon className="text-(--color-text-muted)" />
                )}
              </div>
            </div>

            {/* Actions */}
            {canModify && isHovered && (
              <MessageActions
                isMine={isMine}
                onEdit={() => {
                  const newContent = prompt('Editar mensagem:', message.content ?? '')
                  if (newContent && newContent !== message.content) {
                    onEdit(message.id, newContent)
                  }
                }}
                onDelete={() => onDelete(message.id)}
              />
            )}
          </div>
        </div>
      </div>

      {/* Image lightbox */}
      {lightboxImage && (
        <ImageLightbox
          src={lightboxImage.url}
          alt={lightboxImage.name}
          onClose={() => setLightboxImage(null)}
        />
      )}
    </>
  )
}
